import { useState } from "react";
import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CRITERIA = [
  { key: "pontualidade", label: "Pontualidade" },
  { key: "gramatica", label: "Gramática" },
  { key: "legistica", label: "Legística" },
  { key: "dominio_tema", label: "Domínio do tema" },
  { key: "qualidade_consolidacao", label: "Qualidade da consolidação" },
];

export const RatingForm = ({ docId, initialValue, onSaved, label = "Avaliação do consultor" }) => {
  const [values, setValues] = useState(
    initialValue || {
      pontualidade: 0,
      gramatica: 0,
      legistica: 0,
      dominio_tema: 0,
      qualidade_consolidacao: 0,
    }
  );
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const missing = CRITERIA.find((c) => !values[c.key] || values[c.key] < 1);
    if (missing) {
      toast.error(`Atribua nota ao critério: ${missing.label}`);
      return;
    }
    setSaving(true);
    try {
      await api.post(`/documents/${docId}/rate`, { ...values, comment });
      toast.success("Avaliação registrada.");
      setComment("");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao avaliar.");
    } finally {
      setSaving(false);
    }
  };

  const avg = (
    (values.pontualidade + values.gramatica + values.legistica + values.dominio_tema + values.qualidade_consolidacao) / 5
  ).toFixed(2);

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-xs text-slate-600">Média: <strong className="text-gold-deep">{avg}</strong> / 5</div>
      </div>
      <div className="space-y-2">
        {CRITERIA.map((c) => (
          <div key={c.key} className="flex items-center justify-between gap-2">
            <span className="text-sm text-slate-700">{c.label}</span>
            <StarRating
              value={values[c.key]}
              onChange={(v) => setValues({ ...values, [c.key]: v })}
              testIdPrefix={`rating-${c.key}`}
            />
          </div>
        ))}
      </div>
      <div className="mt-3">
        <Label className="text-xs">Observação (opcional)</Label>
        <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Comentário sobre o trabalho do consultor" className="mt-1" />
      </div>
      <div className="mt-3 flex justify-end">
        <Button onClick={submit} disabled={saving} className="bg-midnight hover:bg-ink text-white" data-testid="submit-rating">
          {saving ? "Salvando..." : "Salvar avaliação"}
        </Button>
      </div>
    </div>
  );
};

export const RatingDisplay = ({ rating, label }) => {
  if (!rating) return null;
  const avg = (
    (rating.pontualidade + rating.gramatica + rating.legistica + rating.dominio_tema + rating.qualidade_consolidacao) / 5
  ).toFixed(2);
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold text-slate-700">{label}</div>
        <div className="text-xs text-slate-600">Média: <strong className="text-gold-deep">{avg}</strong></div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
        {CRITERIA.map((c) => (
          <div key={c.key} className="flex items-center justify-between gap-2 text-xs">
            <span className="text-slate-600">{c.label}</span>
            <StarRating value={rating[c.key]} readOnly size={14} testIdPrefix={`view-${c.key}`} />
          </div>
        ))}
      </div>
    </div>
  );
};
